import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '@/shared/apiClient';
import { authService } from '@/features/auth/services/authService';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';
import { isPersistedProjectId } from '@/features/projects/utils/syntheticDefaultProject';
import { log } from '@/shared/utils/logger';
import { AGENT_DELEGATE_ASSIGNEE_ID, isAgentAssigneeId, normalizeAssigneeValue } from '@/shared/utils/projectDataUtils';

interface UseAssigneeDirectoryParams {
  projectIds: string[];
  userId: string | null;
}

interface ProjectAccessUser {
  id?: string;
  user_id?: string;
  username?: string;
  email?: string;
  role?: string;
  account_role?: string;
  project_role?: string;
  view_permissions?: Record<string, boolean>;
  available_views?: string[];
  assignment_eligible?: boolean;
}

interface AgentProfileResponse {
  id?: string;
  assignee_id?: string;
  assigneeId?: string;
  name?: string;
  status?: string;
  authority?: string;
  model_runtime?: string;
  modelRuntime?: string;
  avatar?: string;
  job_description?: string;
  jobDescription?: string;
  persona?: string;
  capability_tags?: string[];
  capabilityTags?: string[];
  allowed_tools?: string[];
  allowedTools?: string[];
  proof_requirements?: string[];
  proofRequirements?: string[];
  escalation_rules?: string[];
  escalationRules?: string[];
  globally_available?: boolean;
  globallyAvailable?: boolean;
}

export interface ProjectMember {
  id: string;
  username: string;
  email?: string;
  accountRole?: string;
  projectRole?: string;
  role?: string;
  viewPermissions?: Record<string, boolean>;
  availableViews?: string[];
  assignmentEligible?: boolean;
}

export interface AgentProfile {
  id: string;
  assigneeId: string;
  name: string;
  status: string;
  authority: string;
  modelRuntime: string;
  avatar?: string;
  jobDescription: string;
  persona: string;
  capabilityTags: string[];
  allowedTools: string[];
  proofRequirements: string[];
  escalationRules: string[];
  globallyAvailable: boolean;
}

export type TaggableUser = ProjectMember;

const UNKNOWN_ASSIGNEE_LABEL = 'Unknown user';
const VISIBLE_AGENT_LABEL = 'Agent';
const GENERIC_AGENT_PROFILE: AgentProfile = {
  id: 'delegate',
  assigneeId: AGENT_DELEGATE_ASSIGNEE_ID,
  name: VISIBLE_AGENT_LABEL,
  status: 'available',
  authority: 'suggest_only',
  modelRuntime: 'Agent router',
  avatar: 'workflow',
  jobDescription: 'Owns delegated work and routes it to the right internal specialist profile.',
  persona: 'General task owner that selects specialist support behind the scenes.',
  capabilityTags: ['agent', 'delegation'],
  allowedTools: ['suggestions'],
  proofRequirements: ['routing summary'],
  escalationRules: ['Escalate before autonomous mutation or deployment.'],
  globallyAvailable: true,
};
const projectDirectoryCache = new Map<string, ProjectAccessUser[]>();
const projectDirectoryRequests = new Map<string, Promise<void>>();
const projectAgentCache = new Map<string, AgentProfile[]>();
const projectAgentRequests = new Map<string, Promise<void>>();
let projectDirectoryOwner: string | null = null;
const projectDirectoryCacheListeners = new Set<(invalidated: boolean) => void>();

function getDirectoryOwner(userId: string | null): string | null {
  const sessionIdentity = authService.getSessionIdentity();
  return userId && sessionIdentity
    ? JSON.stringify([userId, sessionIdentity, getAdminViewAsUserId()])
    : null;
}

function isCurrentDirectoryOwner(owner: string | null, userId: string | null): boolean {
  return owner !== null && owner === projectDirectoryOwner && owner === getDirectoryOwner(userId);
}

function notifyProjectDirectoryCacheChanged(invalidated = false) {
  projectDirectoryCacheListeners.forEach((listener) => listener(invalidated));
}

export function clearAssigneeDirectoryCache(projectIds?: string[]) {
  if (Array.isArray(projectIds) && projectIds.length > 0) {
    projectIds.forEach((projectId) => {
      projectDirectoryCache.delete(projectId);
      projectDirectoryRequests.delete(projectId);
      projectAgentCache.delete(projectId);
      projectAgentRequests.delete(projectId);
    });
  } else {
    projectDirectoryCache.clear();
    projectDirectoryRequests.clear();
    projectAgentCache.clear();
    projectAgentRequests.clear();
  }
  notifyProjectDirectoryCacheChanged(true);
}

function normalizeAgentProfile(raw: AgentProfileResponse): AgentProfile | null {
  const assigneeId = raw.assignee_id || raw.assigneeId;
  const id = raw.id || (assigneeId?.startsWith('agent:') ? assigneeId.slice('agent:'.length) : assigneeId);
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  if (!id || !assigneeId || !name) {
    return null;
  }

  return {
    id,
    assigneeId,
    name,
    status: raw.status || 'available',
    authority: raw.authority || 'suggest_only',
    modelRuntime: raw.model_runtime || raw.modelRuntime || '',
    avatar: raw.avatar,
    jobDescription: raw.job_description || raw.jobDescription || '',
    persona: raw.persona || '',
    capabilityTags: raw.capability_tags || raw.capabilityTags || [],
    allowedTools: raw.allowed_tools || raw.allowedTools || [],
    proofRequirements: raw.proof_requirements || raw.proofRequirements || [],
    escalationRules: raw.escalation_rules || raw.escalationRules || [],
    globallyAvailable: raw.globally_available ?? raw.globallyAvailable ?? true,
  };
}

async function loadProjectDirectory(projectId: string, owner: string, userId: string): Promise<void> {
  if (!projectId || !isCurrentDirectoryOwner(owner, userId)) return;

  if (projectDirectoryCache.has(projectId)) {
    return;
  }

  const inflight = projectDirectoryRequests.get(projectId);
  if (inflight) {
    await inflight;
    return;
  }

  // The async callback compares its own promise after registration in the map.
  let request!: Promise<void>;
  // eslint-disable-next-line prefer-const -- definite assignment is required for the async self-reference
  request = (async () => {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.projects.access(projectId)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to load project access directory for ${projectId}`);
    }

    const payload = await response.json();
    const users: ProjectAccessUser[] = payload?.data?.access || payload?.access || payload || [];
    if (!isCurrentDirectoryOwner(owner, userId) || projectDirectoryRequests.get(projectId) !== request) return;
    projectDirectoryCache.set(projectId, users);
    notifyProjectDirectoryCacheChanged();
  })();

  projectDirectoryRequests.set(projectId, request);

  try {
    await request;
  } finally {
    if (projectDirectoryRequests.get(projectId) === request) {
      projectDirectoryRequests.delete(projectId);
    }
  }
}

async function loadProjectAgents(projectId: string, owner: string, userId: string): Promise<void> {
  if (!projectId || !isCurrentDirectoryOwner(owner, userId)) return;

  if (projectAgentCache.has(projectId)) {
    return;
  }

  const inflight = projectAgentRequests.get(projectId);
  if (inflight) {
    await inflight;
    return;
  }

  // The async callback compares its own promise after registration in the map.
  let request!: Promise<void>;
  // eslint-disable-next-line prefer-const -- definite assignment is required for the async self-reference
  request = (async () => {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.projects.agents(projectId)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to load project agent directory for ${projectId}`);
    }

    const payload = await response.json();
    const rawAgents: AgentProfileResponse[] = payload?.data?.agents || payload?.agents || payload || [];
    const agents = rawAgents.map(normalizeAgentProfile).filter((agent): agent is AgentProfile => Boolean(agent));
    if (!isCurrentDirectoryOwner(owner, userId) || projectAgentRequests.get(projectId) !== request) return;
    projectAgentCache.set(projectId, agents);
    notifyProjectDirectoryCacheChanged();
  })();

  projectAgentRequests.set(projectId, request);

  try {
    await request;
  } finally {
    if (projectAgentRequests.get(projectId) === request) {
      projectAgentRequests.delete(projectId);
    }
  }
}

export function useAssigneeDirectory({ projectIds, userId }: UseAssigneeDirectoryParams) {
  const [version, setVersion] = useState(0);
  const [cacheRevision, setCacheRevision] = useState(0);
  const owner = getDirectoryOwner(userId);
  // The synthetic empty-catalog project has no access list or agents to load.
  const projectIdsKey = JSON.stringify(Array.from(new Set(projectIds.filter(isPersistedProjectId))));

  useEffect(() => {
    const listener = (invalidated: boolean) => {
      setVersion((current) => current + 1);
      if (invalidated) setCacheRevision((current) => current + 1);
    };
    projectDirectoryCacheListeners.add(listener);
    return () => {
      projectDirectoryCacheListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (owner !== getDirectoryOwner(userId)) return;
    if (projectDirectoryOwner !== owner) {
      projectDirectoryOwner = owner;
      clearAssigneeDirectoryCache();
    }
    if (!owner || !userId) return;

    const uniqueProjectIds: string[] = JSON.parse(projectIdsKey);
    void Promise.all(
      uniqueProjectIds.map(async (projectId) => {
        try {
          await Promise.all([loadProjectDirectory(projectId, owner, userId), loadProjectAgents(projectId, owner, userId)]);
        } catch (error) {
          log.warn('useAssigneeDirectory', `Failed to load assignee directory for project ${projectId}:`, error);
        }
      })
    );
  }, [projectIdsKey, userId, owner, cacheRevision]);

  const { assigneeDirectory, memberDirectories, agentDirectories, allAgents } = useMemo(() => {
    void version; // Cache notifications invalidate this projection without changing project IDs.
    const assigneeDirectory = new Map<string, string>();
    const memberDirectories = new Map<string, ProjectAccessUser[]>();
    const agentDirectories = new Map<string, AgentProfile[]>();
    const allAgents: AgentProfile[] = [];

    if (isCurrentDirectoryOwner(owner, userId)) {
      projectDirectoryCache.forEach((members) => {
        members.forEach((entry) => {
          const memberId = entry?.user_id || entry?.id;
          if (memberId && entry?.username) assigneeDirectory.set(memberId, entry.username);
        });
      });
      projectAgentCache.forEach((agents) => {
        agents.forEach((agent) => {
          assigneeDirectory.set(agent.assigneeId, agent.name);
          allAgents.push(agent);
        });
      });
      const uniqueProjectIds: string[] = JSON.parse(projectIdsKey);
      uniqueProjectIds.forEach((projectId) => {
        memberDirectories.set(projectId, projectDirectoryCache.get(projectId) || []);
        agentDirectories.set(projectId, projectAgentCache.get(projectId) || []);
      });
    }

    return { assigneeDirectory, memberDirectories, agentDirectories, allAgents };
  }, [version, owner, userId, projectIdsKey]);

  const getProjectMemberIds = useCallback(
    (projectId?: string | null) => {
      if (!projectId || !isCurrentDirectoryOwner(owner, userId)) {
        return [];
      }
      return (memberDirectories.get(projectId) || [])
        .map((entry) => entry.user_id || entry.id || '')
        .filter(Boolean);
    },
    [memberDirectories, owner, userId]
  );

  const getTaggableUserIds = useCallback(
    (projectId?: string | null) => getProjectMemberIds(projectId),
    [getProjectMemberIds]
  );

  const getProjectMembers = useCallback((projectId?: string | null): ProjectMember[] => {
    if (!projectId || !isCurrentDirectoryOwner(owner, userId)) {
      return [];
    }
    return (memberDirectories.get(projectId) || [])
      .map((entry) => ({
        id: entry.user_id || entry.id || '',
        username: entry.username || UNKNOWN_ASSIGNEE_LABEL,
        email: entry.email,
        accountRole: entry.account_role,
        projectRole: entry.project_role || entry.role,
        role: entry.role,
        viewPermissions: entry.view_permissions,
        availableViews: entry.available_views,
        assignmentEligible: entry.assignment_eligible !== false,
      }))
      .filter((entry) => entry.id);
  }, [memberDirectories, owner, userId]);

  const getProjectAgents = useCallback((projectId?: string | null): AgentProfile[] => {
    if (!projectId || !isCurrentDirectoryOwner(owner, userId)) {
      return [];
    }
    return agentDirectories.get(projectId) || [];
  }, [agentDirectories, owner, userId]);

  const getAgentProfile = useCallback((assigneeId?: string | null): AgentProfile | undefined => {
    const normalizedAssignee = normalizeAssigneeValue(assigneeId);
    if (!normalizedAssignee) {
      return undefined;
    }

    if (isCurrentDirectoryOwner(owner, userId)) {
      const agent = allAgents.find((candidate) => candidate.assigneeId === normalizedAssignee);
      if (agent) {
        return agent;
      }
    }
    if (isAgentAssigneeId(normalizedAssignee)) {
      return GENERIC_AGENT_PROFILE;
    }
    return undefined;
  }, [allAgents, owner, userId]);

  const getTaggableUsers = useCallback((projectId?: string | null): TaggableUser[] => (
    getProjectMembers(projectId)
  ), [getProjectMembers]);

  const getAssigneeDisplayName = useCallback(
    (assignee: string) => {
      const normalizedAssignee = normalizeAssigneeValue(assignee);
      if (!normalizedAssignee) {
        return 'Unassigned';
      }

      if (isAgentAssigneeId(normalizedAssignee)) {
        return VISIBLE_AGENT_LABEL;
      }
      const resolved = isCurrentDirectoryOwner(owner, userId) ? assigneeDirectory.get(normalizedAssignee) : undefined;
      if (typeof resolved === 'string' && resolved.trim().length > 0) {
        return resolved;
      }
      return UNKNOWN_ASSIGNEE_LABEL;
    },
    [assigneeDirectory, owner, userId]
  );

  return {
    assigneeDirectory,
    getProjectMemberIds,
    getTaggableUserIds,
    getTaggableUsers,
    getProjectMembers,
    getProjectAgents,
    getAgentProfile,
    getAssigneeDisplayName,
  };
}
